import React, { useEffect, useRef, useState } from "react";
import Steper from "../../../../components/d-reusable/Steper";
import CustomCard from "../../../../components/d-reusable/CustomCard";
import { Col, DatePicker, Form, Input, message, Row, Select } from "antd";
import "../style.css";
import CustomPrimaryButton from "../../../../components/d-reusable/CustomPrimaryButton";
import CustomDefaultButton from "../../../../components/d-reusable/CustomDefaultButton";
import CustomDisabledScreen from "../../../../components/d-reusable/CustomDisabledScreen";
import TextArea from "antd/lib/input/TextArea";
import { useDispatch, useSelector } from "react-redux";
import {
  _clearFormData,
  _onUpdatePmCaseField,
} from "../../../../../redux/actions/caseAction";
import moment from "moment";
import { useLocation, useNavigate } from "react-router-dom";
import { createNewAM_Or_PmCaseForCurrentProject } from "../../../../../components/https/projects-apis";
import { errorRequestHandel, getDecimalAge } from "../../../../../helper";
import { usePermissionGuard } from "../../../../../hooks/project-hooks/usePermissionGuard";
import { useSubscriptionGuard } from "../../../../../hooks/subscription-hook/UseSubscriptionGuard";
const { Option } = Select;

const steps = ["Subject Information", "Clinical Evaluation"];

const PMCaseRegistration = () => {
  usePermissionGuard();
  useSubscriptionGuard();
  const [pmCaseForm] = Form.useForm();
  const scrollRef = useRef(null);
  const location = useLocation();
  const { type, projectId, caseId } = location.state || {}; // Destructure state values

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { pmCase } = useSelector((state) => state.cases);
  const { dropdowns, countriesList = [] } = useSelector(
    (state) => state.general
  );
  const subjectInformation = pmCase?.subjectInformation || {};
  const clinicalInformation = pmCase?.clinicalInformation || {};

  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    scrollRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [location]);

  const onNext = () => {
    scrollRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    setActiveStep(1);
  };
  const onBack = () => {
    scrollRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    setActiveStep(0);
  };

  const handleValuesChange = (changedValues, allValues) => {
    const [field, value] = Object.entries(changedValues)[0];
    const section =
      field in subjectInformation
        ? "subjectInformation"
        : "clinicalInformation";

    dispatch(_onUpdatePmCaseField(section, field, value));
  };

  const onFinish = async () => {
    const formattedDOI = subjectInformation?.date_of_accident
      ? moment(subjectInformation.date_of_accident).format("YYYY-MM-DD")
      : null;

    const yearFrom =
      parseInt(clinicalInformation?.estimated_age_from_year) || 0;
    const monthFrom =
      parseInt(clinicalInformation?.estimated_age_from_month) || 0;
    const estimated_age_from = getDecimalAge(yearFrom, monthFrom);

    const toYear = parseInt(clinicalInformation?.estimated_age_to_year) || 0;
    const toMonth = parseInt(clinicalInformation?.estimated_age_to_month) || 0;
    const estimated_age_to = getDecimalAge(toYear, toMonth);

    const {
      estimated_age_from_year,
      estimated_age_from_month,
      estimated_age_to_year,
      estimated_age_to_month,
      ...filteredClinicalInfo
    } = clinicalInformation;

    const data = {
      project_id: projectId,
      ...filteredClinicalInfo,
      ...subjectInformation,
      estimated_age_from,
      estimated_age_to,
      date_of_accident: formattedDOI,
      ...(caseId ? { id: caseId } : {}), // Add id key if caseId is non-zero
    };

    await onCreateCase(data);
  };

  const onCreateCase = async (payload) => {
    try {
      setLoading(true);
      const resposne = await createNewAM_Or_PmCaseForCurrentProject({
        type: type,
        payload: payload,
      });
      if (resposne.status == 200) {
        message.success("PM Case has been created successfully");
        setLoading(false);
        pmCaseForm.resetFields();
        dispatch(_clearFormData());
        navigate(-1);
      }
    } catch (error) {
      setLoading(false);
      errorRequestHandel({ error });
    }
  };

  function validateEstimatedAgeRange(fromYear, fromMonth, toYear, toMonth) {
    const parse = (year, month) => {
      return (parseInt(year) || 0) * 12 + (parseInt(month) || 0);
    };

    if (!fromYear && !fromMonth && !toYear && !toMonth) {
      return { valid: true };
    }

    const fromFilled = fromYear || fromMonth;
    const toFilled = toYear || toMonth;

    if (!fromFilled && toFilled) return { valid: true };
    if (fromFilled && !toFilled) return { valid: true };

    const fromTotal = parse(fromYear, fromMonth);
    const toTotal = parse(toYear, toMonth);

    if (fromTotal >= toTotal) {
      return {
        valid: false,
        message: "'From' age must be less than 'To' age",
      };
    }

    return { valid: true };
  }

  const ageValidator = () => ({
    validator: () => {
      const fromYear = pmCaseForm.getFieldValue("estimated_age_from_year");
      const fromMonth = pmCaseForm.getFieldValue("estimated_age_from_month");
      const toYear = pmCaseForm.getFieldValue("estimated_age_to_year");
      const toMonth = pmCaseForm.getFieldValue("estimated_age_to_month");

      const result = validateEstimatedAgeRange(
        fromYear,
        fromMonth,
        toYear,
        toMonth
      );

      return result.valid
        ? Promise.resolve()
        : Promise.reject(new Error(result.message));
    },
  });

  return (
    <div className="d-case_registration" ref={scrollRef}>
      <CustomDisabledScreen loading={loading} />

      <CustomCard>
        <div className="d-case_form">
          <Form
            form={pmCaseForm}
            layout="vertical"
            className="basic_antForm"
            initialValues={{
              ...subjectInformation,
              ...clinicalInformation,

              date_of_accident: subjectInformation.date_of_accident
                ? moment(subjectInformation.date_of_accident)
                : null,
            }}
            onFinish={onFinish}
            onValuesChange={handleValuesChange}
          >
            <Row gutter={[20, 5]}>
              <Col md={12}>
                <Form.Item
                  label="Temporary Identifier"
                  name="temporary_identifier"
                  rules={[
                    { required: true, message: "This field is required" }, // Validation rules
                  ]}
                >
                  <Input placeholder="Temporary Identifier" />
                </Form.Item>
              </Col>
              <Col md={6}>
                <Form.Item label="Estimated Age Range (From)">
                  <Input.Group compact>
                    <Form.Item name="estimated_age_from_year" noStyle>
                      <Input
                        style={{ width: "48%", marginRight: "4%" }}
                        placeholder="Year"
                        type="number"
                      />
                    </Form.Item>
                    <Form.Item
                      name="estimated_age_from_month"
                      noStyle
                      rules={[
                        {
                          validator(_, value) {
                            if (!value) return Promise.resolve();
                            const month = parseInt(value);
                            if (isNaN(month) || month < 0 || month > 12) {
                              return Promise.reject(
                                new Error("Month must be between 0 and 12")
                              );
                            }
                            return Promise.resolve();
                          },
                        },
                      ]}
                    >
                      <Input
                        style={{ width: "48%" }}
                        placeholder="Months"
                        type="number"
                      />
                    </Form.Item>
                  </Input.Group>
                </Form.Item>
              </Col>

              <Col md={6}>
                <Form.Item label="Estimated Age Range (To)">
                  <Input.Group compact>
                    <Form.Item
                      name="estimated_age_to_year"
                      noStyle
                      rules={[ageValidator()]}
                    >
                      <Input
                        style={{ width: "48%", marginRight: "4%" }}
                        placeholder="Year"
                        type="number"
                      />
                    </Form.Item>
                    <Form.Item
                      name="estimated_age_to_month"
                      noStyle
                      rules={[
                        ageValidator(),
                        {
                          validator(_, value) {
                            if (!value) return Promise.resolve();
                            const month = parseInt(value);
                            if (isNaN(month) || month < 0 || month > 12) {
                              return Promise.reject(
                                new Error("Month must be between 0 and 12")
                              );
                            }
                            return Promise.resolve();
                          },
                        },
                      ]}
                    >
                      <Input
                        style={{ width: "48%" }}
                        placeholder="Months"
                        type="number"
                      />
                    </Form.Item>
                  </Input.Group>
                </Form.Item>
              </Col>

              <Col md={12}>
                <Form.Item label="Nationality" name="nationality">
                  <Select
                    placeholder="Select"
                    showSearch
                    optionFilterProp="children" // Enables filtering based on the Option's text
                    filterOption={(input, option) =>
                      option?.children
                        ?.toLowerCase()
                        .includes(input.toLowerCase())
                    } // Custom filter function
                  >
                    {countriesList?.map((item, index) => {
                      return <Option value={item.id}>{item?.nice_name}</Option>;
                    })}
                  </Select>
                </Form.Item>
              </Col>
              <Col md={12}>
                <Form.Item
                  label="Date of Accident"
                  name="date_of_accident"
                  rules={[
                    { required: true, message: "This field is required" }, // Validation rules
                  ]}
                >
                  <DatePicker
                    placeholder="Date of Accident"
                    style={{ width: "100%", marginTop: 4 }}
                    disabledDate={(current) =>
                      current && current > moment().endOf("day")
                    } // Disable future dates
                  />
                </Form.Item>
              </Col>
              <Col md={12}>
                <Form.Item
                  label="Gender"
                  name="gender"
                  rules={[
                    { required: true, message: "This field is required" }, // Validation rules
                  ]}
                >
                  <Select placeholder="Gender" style={{ marginTop: -5 }}>
                    {dropdowns?.gender?.map((item, index) => {
                      return (
                        <Option value={item.value} key={index}>
                          {item.value}
                        </Option>
                      );
                    })}
                  </Select>
                </Form.Item>
              </Col>

              <Col md={12}>
                <Form.Item name="type_of_accident" label="Type of Accident">
                  <Select style={{ marginTop: -5 }}>
                    {dropdowns?.type_of_accident?.map((item, index) => {
                      return (
                        <Option value={item.value} key={index}>
                          {item.value}
                        </Option>
                      );
                    })}
                  </Select>
                </Form.Item>
              </Col>

              <Col md={12}>
                <Form.Item label="Ethnicity" name="ethnicity">
                  <Select placeholder="Select">
                    {dropdowns?.ethnicity?.map((item, index) => {
                      return (
                        <Option value={item.value} key={index}>
                          {item.value}
                        </Option>
                      );
                    })}
                  </Select>
                </Form.Item>
              </Col>
              <Col md={12}>
                <Form.Item
                  name="location_of_recovery"
                  label="Location of Recovery"
                >
                  <Input placeholder="Location of Recovery" />
                </Form.Item>
              </Col>
              <Col md={12}>
                <Form.Item
                  name="medical_history"
                  label="Medical & Identifying Marks"
                >
                  <TextArea
                    className="form_basic_text_area"
                    placeholder="Write medical & identifying marks"
                  />
                </Form.Item>
              </Col>
              <Col md={12}>
                <Form.Item
                  name="accident_description"
                  label=" Accident Description"
                >
                  <TextArea
                    className="form_basic_text_area"
                    placeholder="Write accident description"
                  />
                </Form.Item>
              </Col>
            </Row>

            <br />
            <Row justify={"start"} gutter={[20, 20]}>
              <Col>
                <CustomPrimaryButton
                  title={"Save"}
                  type={"submit"}
                  isLoading={loading}
                />
              </Col>
            </Row>
          </Form>
        </div>
      </CustomCard>
    </div>
  );
};

export default PMCaseRegistration;

//  <Col md={12}>
//    <Form.Item
//      label="Location of Recovery"
//      name="location_of_recovery"
//      rules={[
//        { required: true, message: "This field is required" }, // Validation rules
//      ]}
//    >
//      <Input placeholder="Write" />
//    </Form.Item>
//  </Col>;
